<template>
  <router-link v-slot="{ href, navigate }" custom :to="to">
    <a
      :href="href"
      class="sp relative my-1 flex items-center rounded-md py-4 text-md font-bold text-primary-100 hover:bg-primary-400 hover:text-white hover:no-underline xl:py-2"
      :class="[
        { 'bg-primary-400 text-white': isCurrent(href) },
        reducedSidebar ? 'justify-center px-1' : 'justify-between gap-4 px-4',
      ]"
      @click="navigate"
    >
      <div
        v-if="collection && notificationsCount(collection) > 0"
        class="absolute -top-1 left-7 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-danger-600"
        :class="[isCurrent(href) ? 'bg-danger-700' : 'bg-danger-400']"
      >
        {{ notificationsCount(collection) }}
      </div>

      <div class="inline-flex w-8 flex-none items-center justify-center">
        <i v-if="icon && !calendar" class="far fa-lg" :class="'fa-' + icon" />
        <div v-if="calendar" class="relative">
          <i class="far fa-calendar text-[24px]"></i>
          <i
            class="far absolute bottom-[7px] left-1/2 -translate-x-1/2 text-[10px]"
            :class="'fa-' + icon"
          ></i>
        </div>
      </div>
      <sidebar-label>{{ title }}</sidebar-label>
      <div
        v-if="!loadingOnboarding && sidebarItemToHighlight === collection"
        class="relative"
      >
        <span class="flex h-3 w-3">
          <span
            class="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-200 opacity-75"
          ></span>
          <span
            class="relative inline-flex h-3 w-3 rounded-full bg-primary-100"
          ></span>
        </span>
      </div>
      <i
        v-if="
          collection && hasConfigurationErrors(collection) && !reducedSidebar
        "
        class="fas fa-exclamation-triangle"
        :class="[isCurrent(href) ? 'text-danger-700' : 'text-danger-400']"
      ></i>
    </a>
  </router-link>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { RouteLocationRaw, useRouter } from 'vue-router';
import { TranslateResult } from 'vue-i18n';
import { Collection } from '@/store/common/collections';
import { useUiStore } from '@/store/ui-store';
import { useOnboarding } from '@/composables/onboarding';
import { useResourceConfig } from '@/composables/useResourceConfig';
import SidebarLabel from '@/components/shared/SidebarLabel.vue';

defineProps({
  collection: {
    type: String as PropType<Collection | null>,
    required: false,
    default: null,
  },
  icon: {
    type: String as PropType<string | null>,
    default: null,
  },
  calendar: {
    type: Boolean,
    default: false,
  },
  to: {
    type: Object as PropType<RouteLocationRaw>,
    required: true,
  },
  title: {
    type: String as PropType<TranslateResult>,
    required: true,
  },
});

const router = useRouter();
const uiStore = useUiStore();
const { sidebarItemToHighlight, loadingOnboarding } = useOnboarding();
const { hasConfigurationErrors } = useResourceConfig();

const reducedSidebar = computed(() => uiStore.reducedSidebar);
const currentHref = computed(
  () => router.resolve(router.currentRoute.value).href,
);

function isCurrent(href: string): boolean {
  return currentHref.value === href;
}

function notificationsCount(collection: Collection): number {
  return uiStore.getNotificationsCount(collection);
}
</script>
